import { Fragment, useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { ArrowUp, Check, ExternalLink, Globe, ImagePlus, X } from "lucide-react";
import type { PostSelectionRequest } from "../hooks/usePostSelectionRequest";
import type { SentReference } from "../hooks/useReferences";
import type { ChatNotice, ConversationController, Outbox } from "../hooks/useConversation";
import { MAX_REFERENCES, type ReferencesController } from "../hooks/useReferences";
import { useStickToBottom } from "../hooks/useStickToBottom";
import { useWorkingLabel } from "../hooks/useWorkingLabel";
import { buildSteps, replyingLabel } from "../lib/buildProgress";
import { MESSAGE_LIMIT } from "../lib/conversationState";
import { cardSlot, replyParagraphs, resultPlacement, showsStaxLabel } from "../lib/simpleChat";
import { Skeleton, SkeletonGroup } from "./Skeleton";
import { CommentChips } from "./Comments";
import { chatSuggestion, type MessageCard, type MessageComment, type MessageIllustration, type MessageSource, type OpenPanel, type PreviewComment } from "../api/contract";
import { Spinner } from "./Spinner";
import { AreaError } from "./area/AreaError";
import { ConfirmationAnswer, ConfirmationCard } from "./ConfirmationCard";
import { AREA_NAME, groupReceipts, receiptsForChanges, type Receipt } from "../lib/receipts";
import type { ShopApi } from "../api/contract";
import { ProductSuggestionCard } from "./shop/ProductSuggestionCard";
import { buyerDataClassName } from "../lib/buyerPrivacy";

interface ConversationBodyProps {
  shopApi?: ShopApi;
  siteId?: string;
  chat: ConversationController;
  /** Something the owner should know before asking for changes. */
  notice?: string;
  /** Cards for work in progress (such as photos being added), shown after the latest messages. */
  review?: ReactNode;
  /** Opens the site's history, from the card under a reply that changed the design. */
  onOpenHistory?: () => void;
  /** The site has no pages yet: the admin opens by getting to know the business. */
  firstSite?: boolean;
  /** Adds an owner's logo to their site media, so the designer can place the real mark. */
  onPickLogo?: (files: File[]) => void;
  /** Brand examples that guide the look, but are never placed on the site. */
  references?: ReferencesController;
  /** Whether the panel is showing; the transcript scrolls to the latest once it is. */
  open?: boolean;
  /** Shows a comment's pin on the page, from its chip on the message that sent it. */
  onShowComment?: (comment: MessageComment) => void;
  /** Finished comment batches, each a card inside Stax's last reply to the message that sent it. */
  results?: { comments: PreviewComment[]; card: (batch: PreviewComment[]) => ReactNode };
  /** On a phone the panel covers the post: brings the writing view back so a suggestion can be chosen there. */
  onShowPost?: () => void;
}

/** Ways to begin, each a sentence the owner finishes in their own words. */
export const STARTERS = [
  { label: "I sell things I make", text: "I make " },
  { label: "I offer a service", text: "I offer " },
];

const PICK_ACCEPT = "image/jpeg,image/png,image/webp,image/heic,image/heif";

const OUTBOX_STATUS: Record<Outbox["status"], string> = {
  sending: "Sending…",
  checking: "Checking it was sent…",
  unconfirmed: "Not confirmed as sent.",
};

/** The inspiration images an owner's message shared, small, above its words. */
function SharedImages({ images }: { images: Array<{ key: string; src: string; alt: string }> }) {
  if (images.length === 0) return null;
  return (
    <span className="bw-msg-refs">
      {images.map((image) => <img key={image.key} src={image.src} alt={image.alt} />)}
    </span>
  );
}

/** A readable form of an address: its host and path. */
function shortUrl(url: string): string {
  try {
    const parsed = new URL(url);
    const path = parsed.pathname === "/" ? "" : parsed.pathname.replace(/\/$/, "");
    return `${parsed.hostname.replace(/^www\./, "")}${path}`;
  } catch { return url; }
}

/** The pages Stax read for a reply: a link to each, and the screenshots it took. */
function MessageSources({ sources }: { sources: MessageSource[] }) {
  const screenshots = sources.flatMap((source) => source.screenshots ?? []);
  return (
    <span className="bw-msg-sources">
      <SharedImages images={screenshots.map((shot) => ({ key: shot.id, src: shot.src, alt: shot.alt }))} />
      <span className="bw-msg-source-list" role="list" aria-label="Pages Stax read">
        {sources.map((source) => (
          <span key={source.url} role="listitem" className="bw-msg-source" data-failed={source.failed ? "" : undefined}>
            <Globe size={14} aria-hidden="true" />
            {/^https?:\/\//.test(source.url) && !source.failed
              ? <a href={source.url} target="_blank" rel="noopener noreferrer" title={source.title}>{shortUrl(source.url)}</a>
              : <span>{shortUrl(source.url)}</span>}
            {source.failed && <span className="bw-msg-source-note">couldn’t open</span>}
          </span>
        ))}
      </span>
    </span>
  );
}

/** The drawings Stax made for a reply: a thumbnail on a checkerboard (white art stays visible), its description, and a way to open it full size. */
export function MessageIllustrations({ illustrations }: { illustrations: MessageIllustration[] }) {
  return (
    <span className="bw-msg-art-list" role="list" aria-label="Drawings Stax made">
      {illustrations.map((art) => (
        <span key={art.id} role="listitem" className="bw-msg-art">
          <a className="bw-msg-art-thumb" href={art.src} target="_blank" rel="noopener noreferrer" aria-label={`Open ${art.alt} full size`}>
            <img src={art.src} alt={art.alt} loading="lazy" />
          </a>
          <span className="bw-msg-art-text">
            <span className="bw-msg-art-alt">{art.alt}</span>
            {!art.placed && <span className="bw-msg-art-note">Not placed</span>}
          </span>
          <a className="bw-msg-art-open" href={art.src} target="_blank" rel="noopener noreferrer">
            Open <ExternalLink size={13} aria-hidden="true" />
          </a>
        </span>
      ))}
    </span>
  );
}

function OutboxMessage({ outbox, onRetry }: { outbox: Outbox; onRetry: () => void }) {
  return (
    <li className="bw-msg" data-role="owner" data-state={outbox.status}>
      <span className="bw-sr">You: </span>
      <SharedImages images={(outbox.attachments ?? []).map((attachment) => ({ key: attachment.asset.sha256, src: attachment.src, alt: "Inspiration image" }))} />
      <span className="bw-msg-bubble">
        {outbox.text}
        <span className="bw-msg-status" role={outbox.status === "unconfirmed" ? "alert" : undefined}>
          {outbox.status !== "unconfirmed" && <Spinner />}
          {OUTBOX_STATUS[outbox.status]}
          {outbox.status === "unconfirmed" && (
            <button type="button" className="bw-btn bw-btn--text" onClick={onRetry}>Send again</button>
          )}
        </span>
      </span>
    </li>
  );
}

function OnboardingPicker({ label, onPick, multiple = false }: {
  label: string; onPick: (files: File[]) => void; multiple?: boolean;
}) {
  const input = useRef<HTMLInputElement>(null);
  return (
    <>
      <button type="button" className="bw-btn bw-btn--quiet" onClick={() => input.current?.click()}>{label}</button>
      <input
        ref={input}
        type="file"
        accept={PICK_ACCEPT}
        multiple={multiple}
        hidden
        onChange={(event) => {
          const files = [...(event.target.files ?? [])];
          event.target.value = "";
          if (files.length) onPick(files);
        }}
      />
    </>
  );
}

/**
 * The ways to answer Stax's greeting on a brand-new site, under its messages until the owner first
 * writes. The greeting itself comes from the server, so it stays in the transcript afterwards.
 */
function OnboardingActions({ onStart, onPickLogo, references }: {
  onStart: (text: string) => void;
  onPickLogo?: (files: File[]) => void;
  references?: ReferencesController;
}) {
  return (
    <>
      {(onPickLogo || references) && (
        <div className="bw-onboarding-uploads" aria-label="Add brand references">
          {onPickLogo && <OnboardingPicker label="Upload my logo" onPick={onPickLogo} />}
          {references && <OnboardingPicker label="Share brand references" multiple onPick={(files) => references.add(files)} />}
          <p>A logo can go on your site. Other references only guide its look and feel. Up to {MAX_REFERENCES} at a time.</p>
        </div>
      )}
      <div className="bw-starters">
        {STARTERS.map((starter) => (
          <button key={starter.label} type="button" className="bw-btn bw-btn--quiet" onClick={() => onStart(starter.text)}>
            {starter.label}
          </button>
        ))}
      </div>
    </>
  );
}

/** Chat-bubble shapes, alternating sides like a real transcript, while the conversation loads. */
function ConversationSkeleton() {
  return (
    <SkeletonGroup label="Loading your conversation" className="bw-messages">
      <div className="bw-msg-skel" data-role="admin"><Skeleton width="70%" height={16} radius={10} /></div>
      <div className="bw-msg-skel" data-role="owner"><Skeleton width="45%" height={16} radius={10} /></div>
      <div className="bw-msg-skel" data-role="admin"><Skeleton width="55%" height={16} radius={10} /></div>
    </SkeletonGroup>
  );
}

/** One line under a reply that changed the design with no comments behind it. */
const REWRITE_STATUS = {
  replaced: "You used this wording.",
  kept: "You kept your own words.",
  retry: "You asked for another suggestion.",
} as const;

/** Stax's suggestion for words selected in a post: compact, with what the owner did with it. The choice itself is made in the writing view. */
function RewriteCard({ card, onShowPost }: { card: Extract<MessageCard, { kind: "post-rewrite" }>; onShowPost?: () => void }) {
  return (
    <div className="bw-rewrite-card" data-decision={card.decision}>
      <span className="bw-rewrite-label">Suggested wording</span>
      <p>{card.replacement}</p>
      {card.decision ? <small>{REWRITE_STATUS[card.decision]}</small>
        : onShowPost ? <button type="button" className="bw-btn bw-btn--quiet bw-rewrite-show" onClick={onShowPost}>Choose in my post</button>
        : <small>Choose Replace, Try again or Keep mine in your post.</small>}
    </div>
  );
}

function SavedStrip({ onOpenHistory }: { onOpenHistory?: () => void }) {
  return (
    <span className="bw-saved-strip">
      <Check size={18} strokeWidth={2.5} aria-hidden="true" />
      <span className="bw-saved-strip-text">Preview updated</span>
      {onOpenHistory && <button type="button" onClick={onOpenHistory}>See history</button>}
    </span>
  );
}

/**
 * What a reply changed, under the area it landed in, with when it goes live. A site with a blog has two answers
 * to "when": the Website's goes with its Publish, a post's with Update post.
 */
export function Receipts({ receipts }: { receipts: readonly Receipt[] }) {
  const groups = groupReceipts(receipts);
  if (groups.length === 0) return null;
  return (
    <div className="bw-receipts">
      {groups.map((group) => group.items.map((item) => (
        <div key={`${group.area}:${item.what}`} className="bw-receipt" data-area={group.area}>
          <span className="bw-receipt-area">{AREA_NAME[group.area]}</span>
          <span className="bw-receipt-text">{item.what && <strong>{item.what}</strong>}<small>{item.goesLive}</small></span>
        </div>
      )))}
    </div>
  );
}

function BuildSteps({ steps }: { steps: NonNullable<ReturnType<typeof buildSteps>> }) {
  return (
    <ol className="bw-steps" aria-label="Building your first draft" aria-busy={steps.some(step => step.state === "active")}>
      {steps.map((step) => (
        <li key={step.label} data-state={step.state}>
          <span className="bw-step-mark" aria-hidden="true">{step.state === "done" && <Check size={14} strokeWidth={3} />}</span>
          <span className="bw-step-text">
            {step.label}
            {step.detail && <span className="bw-step-detail">{step.detail}</span>}
          </span>
          <span className="bw-sr">{step.state === "done" ? " (done)" : step.state === "active" ? " (in progress)" : ""}</span>
        </li>
      ))}
    </ol>
  );
}

export function ConversationBody({ chat, notice, review, onOpenHistory, firstSite = false, onPickLogo, references, open = true, onShowComment, results, onShowPost, shopApi, siteId }: ConversationBodyProps) {
  const { conversation, connection, outbox } = chat;
  const end = useRef<HTMLDivElement>(null);
  const messages = conversation?.messages ?? [];
  const pendingShown = outbox !== null && !messages.some((message) => message.id === outbox.messageId);
  const replying = conversation?.status === "replying";
  const steps = replying ? buildSteps(conversation?.progress) : null;
  const working = useWorkingLabel(conversation?.progress, replying && !steps);
  // Until the owner first writes on a brand-new site, the greeting offers ways to answer it.
  // A move from a card site has no owner message yet but is already working, so it offers nothing.
  const greeting = firstSite && !pendingShown && !replying && !messages.some((message) => message.role === "owner");

  const placement = resultPlacement(messages, results?.comments ?? []);
  const resultCards = (batches: PreviewComment[][] | undefined) => batches?.map((batch) => (
    <Fragment key={batch[0]!.id}>{results!.card(batch)}</Fragment>
  ));

  // The transcript follows the latest while the owner is at the end. Sending a message brings it
  // back there, wherever they had scrolled.
  const { pin } = useStickToBottom(end, open);
  const sentId = pendingShown ? outbox.messageId : null;
  useEffect(() => {
    if (sentId) pin();
  }, [sentId, pin]);

  let transcript: ReactNode;
  if (!conversation) {
    transcript = connection === "reconnecting" ? (
      <AreaError inline message="Your conversation couldn’t be loaded. Trying again automatically." onRetry={() => void chat.refresh()} />
    ) : (
      <ConversationSkeleton />
    );
  } else if (conversation.status === "unavailable" && messages.length === 0) {
    transcript = (
      <div className="bw-unavailable">
        <strong>Chat isn’t switched on yet</strong>
        <p>You can still browse your pages and publish from here.</p>
      </div>
    );
  } else {
    transcript = (
      <>
        {messages.length === 0 && !pendingShown && !firstSite && (
          <p className="bw-conversation-empty">Ask Stax for a change, like new wording or a fresh look. You’ll see it here before anything goes live.</p>
        )}
        <ol className="bw-messages" aria-live="polite" aria-label="Conversation">
          {placement.leading.length > 0 && <li className="bw-msg" data-role="admin">{resultCards(placement.leading)}</li>}
          {messages.map((message, index) => {
            if (message.card?.kind === "confirmation-answered") return <ConfirmationAnswer key={message.id} card={message.card} />;
            const images =<SharedImages images={(message.attachments ?? []).map((attachment) => ({ key: attachment.id, src: attachment.src, alt: attachment.alt }))} />;
            if (message.role === "owner") {
              return (
                <li key={message.id} className={buyerDataClassName("bw-msg", message.buyerData === true)} data-role="owner">
                  <span className="bw-sr">You: </span>
                  {images}
                  {(message.comments?.length || message.text) ? (
                    <span className="bw-msg-bubble">
                      {message.comments?.length ? <CommentChips comments={message.comments} onShow={onShowComment} /> : message.text}
                    </span>
                  ) : null}
                </li>
              );
            }
            const cards = resultCards(placement.after.get(message.id));
            const paragraphs = replyParagraphs(message.text);
            const slot = cardSlot(paragraphs.length);
            const paragraph = (text: string, at: number) => <p key={at}>{text}</p>;
            return (
              <li key={message.id} className={buyerDataClassName("bw-msg", Boolean(message.buyerData || message.changes?.some(change => change.area === "shop" && change.entity === "order")))} data-role="admin">
                <span className="bw-sr">Stax: </span>
                {showsStaxLabel(messages, index) && <span className="bw-msg-who" aria-hidden="true">Stax</span>}
                {images}
                {paragraphs.slice(0, cards ? slot : paragraphs.length).map(paragraph)}
                {cards}
                {cards && paragraphs.slice(slot).map((text, at) => paragraph(text, slot + at))}
                {message.sources?.length ? <MessageSources sources={message.sources} /> : null}
                {message.illustrations?.length ? <MessageIllustrations illustrations={message.illustrations} /> : null}
                {!cards && message.card?.kind === "design-updated" && <SavedStrip onOpenHistory={onOpenHistory} />}
                <Receipts receipts={receiptsForChanges(message.changes ?? [])} />
                {message.card?.kind === "product-suggestion" && shopApi && siteId && <ProductSuggestionCard card={message.card} api={shopApi} siteId={siteId} chat={chat} />}
                {message.card?.kind === "post-rewrite" && <RewriteCard card={message.card} onShowPost={onShowPost} />}
              </li>
            );
          })}
          {pendingShown && <OutboxMessage outbox={outbox} onRetry={chat.retrySend} />}

          {replying && !steps && (
            <li className="bw-typing">
              <Spinner />
              {/* Screen readers hear the stage once, not each time the visible line changes. */}
              <span className="bw-sr">{replyingLabel(conversation.progress)}</span>
              <span key={working} className="bw-typing-text" aria-hidden="true">{working}</span>
            </li>
          )}
        </ol>
        {greeting && (
          <OnboardingActions onPickLogo={onPickLogo} references={references} onStart={(text) => {
            chat.setDraft(text);
            document.getElementById("bw-message")?.focus();
          }} />
        )}
        {steps && <BuildSteps steps={steps} />}
        {conversation.status === "unavailable" && (
          <p className="bw-conversation-empty">Chat is switched off for now. Your earlier messages are kept.</p>
        )}
        {connection === "reconnecting" && <p className="bw-status" role="status"><Spinner /> Reconnecting to StackJe…</p>}
      </>
    );
  }

  return (
    <>
      {notice && <div className="bw-callout" data-tone="attention" role="status"><p>{notice}</p></div>}
      {transcript}
      <ConfirmationCard chat={chat} />
      {review}

      <div ref={end} />
    </>
  );
}

const NOTICES: Record<ChatNotice, string> = {
  busy: "Stax is still working on your last request. Send this when it’s done.",
  limited: "You’ve reached this hour’s limit for requests. Your message is kept here, so try again later.",
  refused: "That message couldn’t be sent. Check it and try again.",
  "switched-off": "Chat was switched off before your message went through. It’s kept here.",
};

/**
 * The image button beside the message box. With inspiration images on offer it opens a small menu:
 * add photos to the site (the photo form), or share up to three images whose look the owner likes.
 * Each choice is a label for its own file input, so one tap opens the picker on every browser.
 */
function ImageMenu({ disabled, onPickPhotos, onPickReferences, room }: {
  disabled: boolean; onPickPhotos?: (files: File[]) => void; onPickReferences?: (files: File[]) => void; room: number;
}) {
  const [open, setOpen] = useState(false);
  const menu = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    // The first choice takes focus, so the keyboard can pick one straight away.
    menu.current?.querySelector<HTMLElement>("[role='menuitem']")?.focus();
    const away = (event: PointerEvent) => { if (!menu.current?.contains(event.target as Node)) setOpen(false); };
    const escape = (event: globalThis.KeyboardEvent) => { if (event.key === "Escape") setOpen(false); };
    document.addEventListener("pointerdown", away);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("pointerdown", away);
      document.removeEventListener("keydown", escape);
    };
  }, [open]);
  // Escape closes the menu only, not the panel around it.
  const closeOnEscape = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "Escape" || !open) return;
    event.stopPropagation();
    setOpen(false);
    menu.current?.querySelector<HTMLElement>("button")?.focus();
  };
  const picker = (onPick: (files: File[]) => void, testId: string) => (
    <input
      type="file"
      accept={PICK_ACCEPT}
      multiple
      hidden
      disabled={disabled}
      data-picker={testId}
      onChange={(event) => {
        const files = [...(event.target.files ?? [])];
        event.target.value = "";
        setOpen(false);
        if (files.length) onPick(files);
      }}
    />
  );
  // A label isn't activated by the keyboard, so Enter and Space open its picker.
  const byKey = (event: KeyboardEvent<HTMLLabelElement>) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    event.currentTarget.querySelector("input")?.click();
  };
  if (!onPickReferences) {
    return onPickPhotos ? (
      <label className="bw-btn bw-btn--icon bw-attach" aria-label="Add photos">
        <ImagePlus size={20} aria-hidden="true" />
        {picker(onPickPhotos, "photos")}
      </label>
    ) : null;
  }
  return (
    <div className="bw-attach-wrap" ref={menu} onKeyDown={closeOnEscape}>
      <button type="button" className="bw-btn bw-btn--icon bw-attach" aria-label="Add images" aria-haspopup="menu" aria-expanded={open} disabled={disabled} onClick={() => setOpen(!open)}>
        <ImagePlus size={20} aria-hidden="true" />
      </button>
      {open && (
        <div className="bw-attach-menu" role="menu">
          {onPickPhotos && (
            <label role="menuitem" tabIndex={0} onKeyDown={byKey}>
              <strong>Add photos to my site</strong>
              <span>They go into your Photos, ready to use.</span>
              {picker(onPickPhotos, "photos")}
            </label>
          )}
          <label role="menuitem" tabIndex={0} aria-disabled={room === 0 || undefined} onKeyDown={byKey}>
            <strong>Share an inspiration image</strong>
            <span>{room === 0 ? `Up to ${MAX_REFERENCES} per message.` : "A website or picture whose look you like. It won’t go on your site."}</span>
            {room > 0 && picker(onPickReferences, "references")}
          </label>
        </div>
      )}
    </div>
  );
}

/** A suggestion that opens the photo picker straight away; the photos then go through the usual photo flow. */
function PhotoSuggestion({ label, onPickPhotos }: { label: string; onPickPhotos: (files: File[]) => void }) {
  // A label isn't activated by the keyboard, so Enter and Space open its picker.
  const byKey = (event: KeyboardEvent<HTMLLabelElement>) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    event.currentTarget.querySelector("input")?.click();
  };
  return (
    <label className="bw-btn bw-btn--quiet" role="button" tabIndex={0} onKeyDown={byKey}>
      <ImagePlus size={16} aria-hidden="true" />
      {label}
      <input
        type="file"
        accept={PICK_ACCEPT}
        multiple
        hidden
        data-picker="suggestion-photos"
        onChange={(event) => {
          const files = [...(event.target.files ?? [])];
          event.target.value = "";
          if (files.length) onPickPhotos(files);
        }}
      />
    </label>
  );
}

/** The inspiration images waiting to go with the next message, each removable, "Looking…" until it's ready. */
function PendingReferences({ references }: { references: ReferencesController }) {
  if (references.items.length === 0) return null;
  return (
    <ul className="bw-refs" aria-label="Inspiration images to send">
      {references.items.map((item, index) => (
        <li key={item.key} data-status={item.status}>
          <img src={item.src} alt={`Inspiration image ${index + 1}`} />
          {item.status === "looking" && <span className="bw-refs-state" role="status"><Spinner /> Looking…</span>}
          {item.status === "failed" && <span className="bw-refs-state" role="alert">Couldn’t add</span>}
          <button type="button" className="bw-refs-remove" aria-label={`Remove inspiration image ${index + 1}`} onClick={() => references.remove(item.key)}>
            <X size={14} aria-hidden="true" />
          </button>
        </li>
      ))}
    </ul>
  );
}

export function Composer({ chat, onPickPhotos, references, onOpenPanel, blogAvailable = true, selection, onCancelSelection, onSend }: {
  selection?: PostSelectionRequest;
  onCancelSelection?: () => void;
  onSend?: (text: string, attachments: SentReference[]) => boolean;
  chat: ConversationController; onPickPhotos?: (files: File[]) => void;
  /** Opens a panel of the workspace, for the buttons where Stax points the owner to one. */
  onOpenPanel?: (panel: OpenPanel, suggestion?: { name?: string; base?: string }) => void;
  /** Inspiration images for the next message; without it, the image button only adds photos. */
  references?: ReferencesController;
  /** False when the platform hasn't turned the blog on for this site: buttons that open the blog panel are omitted. */
  blogAvailable?: boolean;
}) {
  const { conversation, draft, setDraft, notice, outbox } = chat;
  const [dropped, setDropped] = useState(false);
  // Grows with the words (Enter adds a line) up to the CSS max-height, then scrolls.
  const field = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    const element = field.current;
    if (!element) return;
    element.style.height = "auto";
    element.style.height = `${element.scrollHeight + element.offsetHeight - element.clientHeight}px`;
    // The placeholder changes with a waiting confirmation; its height must follow.
  }, [draft, chat.confirmation]);
  useEffect(() => { if (selection) field.current?.focus(); }, [selection]);
  const off = !conversation || conversation.status === "unavailable";
  const awaiting = conversation?.status === "awaiting-input";
  const attached = references?.items.length ?? 0;
  // With images, the words are optional; sending waits until each image is ready.
  const imagesReady = attached > 0 && !references!.looking && !references!.failed;
  const canSend = (!selection || Boolean(draft.trim())) && (attached > 0 ? !awaiting && chat.ready && imagesReady && draft.trim().length <= MESSAGE_LIMIT : chat.canSend);
  const send = () => {
    if (!canSend) return;
    if (attached === 0) { if (onSend) onSend(draft, []); else chat.send(); return; }
    const sent = references!.take();
    const accepted = onSend ? onSend(draft, sent) : chat.send(undefined, sent);
    if (!accepted) references!.restore(sent);
    setDropped(false);
  };
  const suggestions = !selection && conversation?.status === "ready" && !outbox ? conversation.suggestions.map(chatSuggestion).filter((suggestion) => blogAvailable || !(suggestion.action === "open" && suggestion.panel === "blog")) : [];
  /** A button asking for something only the owner can type: start their answer and put the cursor after it. */
  const startAnswer = (text: string) => {
    if (!draft.trim()) setDraft(text);
    const element = field.current;
    if (!element) return;
    element.focus();
    requestAnimationFrame(() => { const end = element.value.length; element.setSelectionRange(end, end); });
  };
  const remaining = MESSAGE_LIMIT - draft.trim().length;
  const placeholder = off
    ? "Chat isn’t switched on yet"
    : conversation.status === "replying" || outbox
      ? "Type your next message…"
      : chat.confirmation
        ? "Answer the question above"
      : awaiting
        ? "Reply to continue the design…"
      : selection ? "How should Stax change these words?" : "Message Stax";

  let note: { text: string; alert: boolean } | null = null;
  if (notice) note = { text: NOTICES[notice], alert: true };
  else if (references?.failed) note = { text: "An image couldn’t be added. Remove it to send your message.", alert: true };
  else if (dropped) note = { text: `You can share up to ${MAX_REFERENCES} inspiration images in a message.`, alert: false };
  else if (references?.looking) note = { text: "Stax is looking at your image. You can send once it’s ready.", alert: false };
  else if (remaining < 0) note = { text: `${-remaining} characters over the limit. Shorten it to send.`, alert: true };
  else if (remaining < 400) note = { text: `${remaining} characters left`, alert: false };

  return (
    <>
      {suggestions.length > 0 && (
        <div className="bw-suggestions" aria-label="Suggestions">
          {suggestions.map((suggestion) => suggestion.action === "add-photos" ? (
            onPickPhotos ? <PhotoSuggestion key={suggestion.label} label={suggestion.label} onPickPhotos={onPickPhotos} /> : null
          ) : suggestion.action === "type" ? (
            <button key={suggestion.label} type="button" className="bw-btn bw-btn--quiet" onClick={() => startAnswer(suggestion.text ?? "")}>
              {suggestion.label}
            </button>
          ) : suggestion.action === "open" ? (
            onOpenPanel ? <button key={suggestion.label} type="button" className="bw-btn bw-btn--quiet" onClick={() => onOpenPanel(suggestion.panel, suggestion)}>{suggestion.label}</button> : null
          ) : (
            <button key={suggestion.label} type="button" className="bw-btn bw-btn--quiet" onClick={() => chat.send(suggestion.label)}>
              {suggestion.label}
            </button>
          ))}
        </div>
      )}
      {selection && <div className="bw-composer-selection" id="bw-selected-words">
        <div><strong>Selected words</strong><button type="button" className="bw-btn bw-btn--text" aria-label="Remove selected words" onClick={onCancelSelection}><X size={16} aria-hidden="true" /></button></div>
        <blockquote>{selection.selection.text}</blockquote>
        <p>Describe the change, then send your request to Stax.</p>
      </div>}
      {references && <PendingReferences references={references} />}
      <form
        className="bw-composer"
        onSubmit={(event) => {
          event.preventDefault();
          send();
        }}
      >
        <ImageMenu
          disabled={off || awaiting}
          onPickPhotos={onPickPhotos}
          onPickReferences={references ? (files) => setDropped(references.add(files) > 0) : undefined}
          room={Math.max(0, MAX_REFERENCES - attached)}
        />
        <label htmlFor="bw-message" className="bw-sr">Message Stax</label>
        <textarea
          id="bw-message"
          ref={field}
          rows={1}
          aria-keyshortcuts="Meta+Enter Control+Enter"
          value={draft}
          disabled={off}
          placeholder={attached > 0 && !off && !selection ? "Add a note (optional)" : placeholder}
          aria-describedby={selection ? "bw-selected-words" : undefined}
          aria-invalid={remaining < 0 || undefined}
          onChange={(event) => {
            setDraft(event.target.value);
            if (notice) chat.dismissNotice();
          }}
          // Enter starts a new line; Cmd/Ctrl+Enter sends, like the Send button.
          onKeyDown={(event) => {
            if (event.key === "Enter" && (event.metaKey || event.ctrlKey) && !event.nativeEvent.isComposing) {
              event.preventDefault();
              send();
            }
          }}
        />
        <button type="submit" className="bw-btn bw-btn--primary" disabled={!canSend} aria-label="Send message">
          <ArrowUp size={20} aria-hidden="true" />
        </button>
      </form>
      {note && <p className="bw-composer-note" role={note.alert ? "alert" : undefined}>{note.text}</p>}
    </>
  );
}
